import { formatCurrency } from './helper';

export const getTabDataMap = ({
  cardsData,
  bottomCardData,
  riskStratificationData,
  CostTrendChartType,
  tableData,
}: {
  cardsData: any[];
  bottomCardData: any[];
  riskStratificationData: any[];
  CostTrendChartType: any;
  tableData: any[];
}): Record<string, boolean> => {
  return {
    Documents: cardsData?.length > 0,
    'Claims Analysis': bottomCardData?.some(
      (card: any) => card?.items?.length > 0,
    ),
    'Risk Stratification': riskStratificationData.some(
      (tier: any) => tier?.memberIds?.length > 0,
    ),
    'Spend Trend': CostTrendChartType?.medicalClaimsData?.length > 0,
    'Task Board': tableData?.length > 0,
    'Care Gaps': riskStratificationData?.some(
      (tier: any) => tier?.memberIds?.length > 0,
    ),
  };
};

export const monthOptions = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const generateQuarterTabs = (
  renewalMonth: string,
  baseYear: number,
): { label: string; months: { month: string; year: number }[] }[] => {
  const startIdx = monthOptions.indexOf(renewalMonth);
  if (startIdx === -1) {
    return [];
  }

  const tabs = [];

  for (let i = 0; i < 4; i++) {
    const months = [];
    for (let j = 0; j < 3; j++) {
      const idx = startIdx + i * 3 + j;
      const month = monthOptions[idx % 12];
      const yearOffset = Math.floor((startIdx + i * 3 + j) / 12);
      const year = baseYear + yearOffset;
      months.push({ month, year });
    }

    const label = `Q${i + 1} (${months[0].month.slice(0, 3)} ${
      months[0].year % 100
    } - ${months[2].month.slice(0, 3)} ${months[2].year % 100})`;

    tabs.push({ label, months });
  }

  return tabs;
};

export const getQuarterMonthRanges = (renewalMonth: string): number[][] => {
  const monthOptions = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const startIdx = monthOptions.indexOf(renewalMonth);
  if (startIdx === -1) {
    return [];
  }

  const quarters: number[][] = [];

  for (let i = 0; i < 4; i++) {
    const months: number[] = [];
    for (let j = 0; j < 3; j++) {
      months.push((startIdx + i * 3 + j) % 12);
    }
    quarters.push(months);
  }

  return quarters;
};

export const getQuarterIndexFromDate = (
  date: Date,
  renewalMonth: string,
): number => {
  const startIdx = monthOptions.indexOf(renewalMonth);
  if (startIdx === -1) {
    return -1;
  }

  const month = date.getMonth();

  for (let i = 0; i < 4; i++) {
    const start = (startIdx + i * 3) % 12;
    const end = (startIdx + i * 3 + 2) % 12;

    if (start <= end) {
      if (month >= start && month <= end) {
        return i;
      }
    } else {
      if (month >= start || month <= end) {
        return i;
      }
    }
  }

  return -1;
};

type Activity = any;

export const groupActivitiesByQuarter = (
  activities: Activity[],
  renewalMonth: string,
): { quarterIndex: number; data: Activity[] }[] => {
  const result: { quarterIndex: number; data: Activity[] }[] = [
    { quarterIndex: 0, data: [] },
    { quarterIndex: 1, data: [] },
    { quarterIndex: 2, data: [] },
    { quarterIndex: 3, data: [] },
  ];
  activities.forEach((activity) => {
    const date = new Date(
      activity?.start_date ??
        activity?.suggested_start_date ??
        activity?.createdAt,
    );
    const qIndex = getQuarterIndexFromDate(date, renewalMonth);
    if (qIndex !== -1) {
      result[qIndex].data.push(activity);
    }
  });

  return result;
};
export const groupRiskByQuarter = (
  activities: Activity[],
  renewalMonth: string,
): {
  quarterIndex: number;
  data: Activity[];
}[] => {
  const result: { quarterIndex: number; data: Activity[] }[] = [
    { quarterIndex: 0, data: [] },
    { quarterIndex: 1, data: [] },
    { quarterIndex: 2, data: [] },
    { quarterIndex: 3, data: [] },
  ];
  activities?.forEach((activity) => {
    const date = new Date(activity?.date_identified);
    const qIndex = getQuarterIndexFromDate(date, renewalMonth);
    if (qIndex !== -1) {
      result[qIndex].data.push(activity);
    }
  });

  return result;
};

export const getFormatterForTitle = (title: string) => {
  const lowerTitle = title.toLowerCase();

  if (lowerTitle.includes('top spending')) {
    return (val: number) => formatCurrency(val); // Currency formatter
  }

  if (lowerTitle.includes('surge') || lowerTitle.includes('categories')) {
    return (val: number) => {
      let formatted: string;

      if (val > -1 && val < 1) {
        // One decimal place for values between -1 and 1
        formatted = val?.toFixed(1);
        if (formatted === '0.0') {
          formatted = '0';
        }
      } else {
        // Round to nearest integer for other values
        formatted = Math.round(val).toLocaleString('en-US');
      }

      return `${formatted}%`;
    };
  }

  return (val: number) => val.toString(); // Default fallback
};
